/**
 * GHS H-code → pictogram derivation — single core shared by
 * `packages/api`'s `GHSService` and the apps' pictogram preview helpers
 * (F12 P1-12). Built deterministically from `ghsHCodes` (read-only single
 * source of truth); no inline duplicate of the H-code data.
 *
 * Keys keep the original casing — official codes include mixed case
 * (H350i, H360Fd, H361f) — so lookups must be exact-first then
 * case-insensitive.
 */

import { ghsHCodes } from '../data'

/** H-code → GHS pictogram-code map (codes with an empty pictogram are skipped). */
export const GHS_HCODE_PICTOGRAM_MAP: Record<string, string> = (() => {
  const map: Record<string, string> = {}
  for (const [code, info] of Object.entries(ghsHCodes)) {
    const pictogram = info?.pictogram
    if (typeof pictogram === 'string' && pictogram) {
      map[code] = pictogram
    }
  }
  return map
})()

const GHS_HCODE_PICTOGRAM_BY_LOWER: Record<string, string> = (() => {
  const map: Record<string, string> = {}
  for (const [code, pictogram] of Object.entries(GHS_HCODE_PICTOGRAM_MAP)) {
    if (!map[code.toLowerCase()]) map[code.toLowerCase()] = pictogram
  }
  return map
})()

/** Pictogram code for a single H-code (undefined when the code maps to none). */
export function pictogramCodeForHCode(hCode: string): string | undefined {
  const code = String(hCode).trim()
  return GHS_HCODE_PICTOGRAM_MAP[code] ?? GHS_HCODE_PICTOGRAM_BY_LOWER[code.toLowerCase()]
}

/**
 * Unique GHS pictogram codes derived from a list of H-codes, in first-seen
 * H-code order (each pictogram appears at most once).
 */
export function derivePictogramCodesFromHCodes(
  hCodes: Array<string | null | undefined> | null | undefined,
): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of hCodes ?? []) {
    if (raw == null) continue
    const pictogram = pictogramCodeForHCode(String(raw))
    if (!pictogram || seen.has(pictogram)) continue
    seen.add(pictogram)
    out.push(pictogram)
  }
  return out
}
